import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { JsonViewerComponent } from '../json-viewer/json-viewer.component';

// ────────────────────────────────────────────────────────────────────────────
// Data viewer / editor
// ────────────────────────────────────────────────────────────────────────────

export interface NetworkDataDialogData {
  title: string;
  payload: unknown;
  editable?: boolean;
}

@Component({
  selector: 'app-network-data-dialog',
  standalone: true,
  imports: [FormsModule, MatDialogModule, MatButtonModule, MatIconModule, JsonViewerComponent],
  template: `
    <div class="data-dialog">
      <div class="data-header">
        <h2>{{ data.title }}</h2>
        <button mat-icon-button (click)="copy()" title="Copy to clipboard">
          <mat-icon>content_copy</mat-icon>
        </button>
      </div>

      <div mat-dialog-content class="data-body">
        <app-json-viewer
          [data]="data.payload"
          [mode]="data.editable ? 'edit' : 'view'"
          [resizable]="true"
          [initialHeight]="360"
          [minHeight]="120"
          (save)="onViewerSave($event)" />
      </div>

      <div mat-dialog-actions align="end">
        <button mat-button mat-dialog-close>Close</button>
      </div>
    </div>
  `,
  styles: [`
    .data-dialog { min-width: 540px; max-width: 80vw; }
    .data-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 24px 0 24px;
    }
    .data-header h2 { margin: 0; font-size: 1.1rem; }
    .data-body { padding-top: 8px; }
  `],
})
export class NetworkDataDialog {
  readonly data: NetworkDataDialogData = inject(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<NetworkDataDialog>);

  copy(): void {
    const v = this.data.payload;
    const text = v === null || v === undefined ? ''
      : typeof v === 'string' ? v
      : JSON.stringify(v, null, 2);
    navigator.clipboard.writeText(text);
  }

  onViewerSave(payload: unknown): void {
    this.dialogRef.close(payload);
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Save-to-favorites dialog (prompts for a name)
// ────────────────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-save-favorite-dialog',
  standalone: true,
  imports: [FormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule],
  template: `
    <h2 mat-dialog-title>Save to favorites</h2>
    <mat-dialog-content>
      <mat-form-field appearance="outline" class="full">
        <mat-label>Name</mat-label>
        <input matInput [(ngModel)]="name" cdkFocusInitial (keyup.enter)="submit()">
      </mat-form-field>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Cancel</button>
      <button mat-flat-button color="primary" [disabled]="!name.trim()" (click)="submit()">Save</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .full { width: 320px; }
    :host ::ng-deep .mat-mdc-dialog-content { padding-top: 8px !important; }
  `],
})
export class SaveFavoriteDialog {
  private readonly dialogRef = inject(MatDialogRef<SaveFavoriteDialog>);
  name = '';

  submit(): void {
    const trimmed = this.name.trim();
    if (trimmed) this.dialogRef.close(trimmed);
  }
}
